import { Currency, formatAmount, formatAmountFromString } from '@epilot/pricing'

import { getConfig } from 'EnvConfig'
import { i18n } from 'i18n/config'

import { EntityItem } from '../api/ApiTypes'
import { EntityAction } from '../context/PortalConfigContext'

import { getCurrentLanguage, Locale } from './LanguageUtils'
import { Address } from './OrderUtils'

export enum RequestStatus {
  Active = 'active',
  Draft = 'draft',
  Quote = 'quote',
  Placed = 'placed',
  Cancelled = 'cancelled',
  Complete = 'complete',
  OpenForAcceptance = 'open_for_acceptance'
}

const getQueryParams = (query: string): Record<string, string> => {
  const params = new URLSearchParams(query)

  const queryParams: Record<string, string> = {}

  params.forEach((value, key) => {
    queryParams[key] = value
  })

  return queryParams
}

export const getLocaleDateString = (language: Locale) => {
  switch (language) {
    case Locale.EN:
      return `en-GB`

    case Locale.DE:
      return `de-DE`

    default:
      return `en-GB`
  }
}

export const isEntityHydrated = (entity: EntityItem): boolean => {
  // look up for any relation in the entity
  for (const attribute in entity) {
    if (
      typeof entity[attribute] === 'object' &&
      entity[attribute] !== null &&
      ('$relation_ref' in entity[attribute] || '$relation' in entity[attribute])
    )
      return false
  }

  return true
}

const getEcpDomain = (): string => {
  const pathWithoutQueryData = window.location.href.split('?')[0]

  if (window.AppShell) {
    return window.AppShell.portalDomain
  }

  return window.location.href.includes('localhost')
    ? getConfig().DEPLOYED_ECP_URL
    : pathWithoutQueryData
}

const getFiles = (files: any[]): EntityItem[] => {
  if (!files?.length) {
    return []
  }

  return files.filter((file: EntityItem) => file.filename)
}

const toLocalePrice = (
  price: number,
  currency: Currency | string,
  locale = 'de-DE',
  enableSubunitDisplay = false
): string => {
  const currencyCode =
    typeof currency === 'string' ? currency : currency ?? 'EUR'

  try {
    return formatAmount({
      amount: price ?? 0,
      currency: currencyCode as Currency,
      format: '$0,0.00',
      locale: locale,
      enableSubunitDisplay
    })
  } catch (error) {
    console.error('Error formatting price', {
      price,
      currency,
      locale,
      enableSubunitDisplay
    })
    console.error(error)

    return ''
  }
}

const toLocalePriceFromString = (
  price: string,
  currency: Currency | string,
  locale = 'de-DE',
  enableSubunitDisplay = false,
  options?: Partial<Parameters<typeof formatAmountFromString>[0]>
): string => {
  const currencyCode =
    typeof currency === 'string' ? currency : currency ?? 'EUR'

  try {
    return formatAmountFromString({
      decimalAmount: price ?? '',
      currency: currencyCode as Currency,
      format: '$0,0.00',
      locale: locale,
      enableSubunitDisplay,
      ...(options || {})
    })
  } catch (error) {
    console.error('Error formatting price', {
      price,
      currency,
      locale,
      enableSubunitDisplay,
      options
    })
    console.error(error)

    return ''
  }
}

export const formatAddress = (address?: Address): string => {
  const trimmedAddress: string = `${
    address?.additional_info ? `${address?.additional_info}, ` : ''
  }${address?.street || ''} ${address?.street_number || ''}, ${
    address?.postal_code || ''
  } ${address?.city || ''} ${address?.country || ''}`.trim()

  if (trimmedAddress === ',') {
    return ''
  }

  return trimmedAddress
}

const toLocaleDateString = (dateObject: Date): string => {
  const locale = i18n.language

  const localeDate = dateObject.toLocaleDateString(
    getLocaleDateString(i18n.language as Locale),
    {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }
  )

  if (locale === 'de-DE') {
    const [day, month, year] = localeDate.split('.')

    return `${day}.${month}.${year}`
  }

  if (locale === 'en') {
    const [day, month, year] = localeDate.split('/')

    return `${day}.${month}.${year}`
  }

  return localeDate
}

const toLocaleDate = (date: string | null, default_value?: string): string => {
  if (!date) {
    return default_value || '-'
  }

  const dateObject = new Date(date)

  return toLocaleDateString(dateObject)
}

const toLocaleDateAndTime = (date: string, default_value?: string): string => {
  if (!date) {
    return default_value || '-'
  }

  const dateObject = new Date(date)

  return dateObject.toLocaleTimeString(
    getLocaleDateString(i18n.language as Locale),
    {
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }
  )
}

const isPlaceholderDate = (date: string) => {
  if (typeof date !== 'string') return false

  // There are the known placeholders our customers use
  const PLACEHOLDERS = ['0000-00-00', '1970-01-01', '2099-12-31', '9999-12-31']

  return PLACEHOLDERS.some((placeholder) => date.startsWith(placeholder))
}

const getActionsForEntity = (
  actions: EntityAction[],
  entity: string
): EntityAction[] => {
  return actions?.filter((action) => action.slug === entity)
}

const wait = (time = 1000) =>
  new Promise((resolve) => setTimeout(resolve, time))

/**
 * Ensure URL contains a protocol with a selected fallback
 */
const ensureUrlProtocol = (url?: string | null, fallback = '//') => {
  if (!url || typeof url !== 'string') return url

  return url.match(/^(\w+:\/\/|\/\/)/) ? url : `${fallback}${url}`
}

const formatNumber = (input: number, options?: Intl.NumberFormatOptions) =>
  new Intl.NumberFormat(getCurrentLanguage(), options).format(input)

/**
 * Limit the execution of the functionality to once per delay period.
 *
 * Compared to the common throttle, it works even if the user re-opens the page.
 * As such, it requires an identifier and is more useful with large delays like a minute.
 *
 * @param id Identifier of the functionality to throttle.
 * @param fn Function to throttle.
 * @param delaySeconds Delay in seconds.
 * @param delayFn Function that can proactively delay the execution.
 */
const throttleAcrossVisits =
  <T extends unknown[]>(
    id: string | ((...args: T) => string),
    fn: (...args: T) => void,
    delaySeconds: number,
    delayFn?: (delayExecution: () => void) => void
  ) =>
  (...args: T) => {
    const throttleId = typeof id === 'function' ? id(...args) : id

    const lastCall =
      Number(
        window.localStorage.getItem(`throttleAcrossVisits:${throttleId}`)
      ) || 0

    const now = Date.now()

    if (now - lastCall >= delaySeconds * 1000) {
      fn(...args)

      window.localStorage.setItem(
        `throttleAcrossVisits:${throttleId}`,
        now.toString()
      )
    }

    if (typeof delayFn === 'function') {
      delayFn(() => {
        window.localStorage.setItem(
          `throttleAcrossVisits:${throttleId}`,
          Date.now().toString()
        )
      })
    }
  }

const isJsonEmpty = (jsonObject) => {
  return Object.keys(jsonObject).length === 0
}

export default {
  wait,
  getEcpDomain,
  getFiles,
  toLocalePrice,
  toLocalePriceFromString,
  toLocaleDate,
  toLocaleDateString,
  toLocaleDateAndTime,
  isPlaceholderDate,
  getActionsForEntity,
  ensureUrlProtocol,
  getQueryParams,
  formatNumber,
  throttleAcrossVisits,
  isJsonEmpty
}

export const VALIDITY_SORT_ORDER: Record<string, number> = {
  current: 1,
  future: 2,
  past: 3,
  unknown: 4
}

export function getTimeSliceValidity(
  start: string,
  end: string,
  date: string
): 'past' | 'future' | 'current' {
  if (start && end) {
    if (date >= start && date <= end) {
      return 'current'
    }
    if (date > end) {
      return 'past'
    }

    return 'future'
  }

  // start only
  if (start && !end) {
    if (date >= start) {
      return 'current'
    }

    return 'future'
  }

  // end only
  if (date > end) {
    return 'past'
  }

  return 'current'
}

/** Transform underscore-separated string to title-case human-readable format */
export const humanizeUnderscore = (str: string): string => {
  if (!str) return ''

  return str
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase())
}

export const isNonNullable = <T>(value: T): value is NonNullable<T> =>
  value !== undefined && value !== null

export const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Returns true if the given string is a valid UUID (v1–v5). */
export const isValidUuid = (value: string | null | undefined): boolean => {
  if (!value) return false

  return UUID_REGEX.test(value)
}

/** Checks if a string contains HTML content using the browser's native parser. */
export const isHtml = (str: string): boolean => {
  if (typeof str !== 'string' || !str.trim()) return false

  const doc = new DOMParser().parseFromString(str, 'text/html')

  return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1)
}

/**
 * Function that will check if a given object is empty, e.g. has no keys
 * @param obj - object to check if it is empty
 * @returns boolean - true if object is not empty, false otherwise
 */
export function isNonEmptyObject(
  obj?: Record<string, unknown> | null
): boolean {
  if (!obj) return false

  return Object.keys(obj).length > 0
}
